import { observer } from 'mobx-react';
import { useCallback, useEffect, useRef } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import { Button, Flex, Icon, Text } from '@servicetitan/anvil2';
import IconLock from '@servicetitan/anvil2/assets/icons/material/round/lock.svg';
import { provide, useDependencies } from '@servicetitan/react-ioc';

import { AgentSuggestedPrompts } from './agent-suggested-prompts';
import { ChatHeaderNew } from '../../common/components/chat-layout/chat-header-new';
import { ChatLayout } from '../../common/components/chat-layout/chat-layout';
import { Markdown } from '../../common/components/entity-components/markdown/markdown';
import { FooterRow } from '../../common/components/footer-row/footer-row';
import { Loader } from '../../common/components/loader/loader';
import { MessageBubble } from '../../common/components/message-bubble/message-bubble';
import { MessageRow } from '../../common/components/message-row/message-row';
import { ReportSections } from '../../common/components/report-sections/report-sections';
import { MessageType } from '../../common/models/message-type.model';
import { ChatStore, IMessage } from '../../common/stores/chat.store';
import { decodeAtlasPromptParam } from '../../common/utils/decode-atlas-prompt-param';
import { navigateToClose } from '../../common/utils/navigate-to-close';
import { AgentChatStore } from '../stores/agent-chat.store';

interface AgentChatParams {
    topicSlug: string;
    entry: string;
    entryId?: string;
    promptToken?: string;
}

export const AgentChatPage = provide({ singletons: [AgentChatStore] })(
    observer(() => {
        const { topicSlug, entry, entryId, promptToken } = useParams<AgentChatParams>();
        const [chatStore, agentChatStore] = useDependencies<[ChatStore, AgentChatStore]>(
            ChatStore,
            AgentChatStore
        );
        const history = useHistory();
        const location = useLocation();

        const handleBack = () => {
            history.goBack();
        };
        const handleClose = () => {
            navigateToClose();
        };
        const fromInApp = new URLSearchParams(location.search).get('fromInApp') === '1';
        const showClose = !fromInApp;
        const autoPromptRef = useRef<string | undefined>(undefined);
        const promptSentRef = useRef(false);
        /*
         * Init/dispose is tied to the base route params only. The `promptToken` path
         * segment is intentionally excluded from the key: the auto-send effect strips it
         * via history.replace, and keying on it would run dispose()+init() and wipe the
         * chat mid-session. Snapshot the decoded prompt when the base route identity changes.
         */
        const routeKey = `${topicSlug}/${entry}/${entryId ?? ''}`;
        const initRouteKeyRef = useRef('');
        if (initRouteKeyRef.current !== routeKey) {
            initRouteKeyRef.current = routeKey;
            autoPromptRef.current = decodeAtlasPromptParam(promptToken);
            promptSentRef.current = false;
        }

        useEffect(() => {
            agentChatStore.init(topicSlug, entry, entryId, autoPromptRef.current);
            return () => {
                agentChatStore.dispose();
            };
        }, [agentChatStore, topicSlug, entry, entryId]);

        useEffect(() => {
            if (agentChatStore.status !== 'ready' || promptSentRef.current) {
                return;
            }
            const prompt = autoPromptRef.current;
            if (!prompt) {
                return;
            }
            promptSentRef.current = true;
            agentChatStore.handleMessage(prompt);

            const questionsPath = entryId
                ? `/topic/${topicSlug}/${entry}/${entryId}/questions`
                : `/topic/${topicSlug}/${entry}/questions`;
            history.replace({
                pathname: questionsPath,
                search: location.search,
            });
        }, [
            agentChatStore,
            agentChatStore.status,
            history,
            topicSlug,
            entry,
            entryId,
            location.search,
        ]);

        const handleMessage = useCallback(
            (message: string) => agentChatStore.handleMessage(message),
            [agentChatStore]
        );

        if (agentChatStore.status === 'unknown-agent') {
            return (
                <Flex grow={1} alignItems="center" justifyContent="center" className="p-4">
                    <Text>Agent not found.</Text>
                </Flex>
            );
        }

        if (agentChatStore.status === 'locked') {
            return (
                <Flex
                    grow={1}
                    direction="column"
                    alignItems="center"
                    justifyContent="center"
                    gap="2"
                    className="p-4"
                >
                    <Icon svg={IconLock} size="large" />
                    <Text size="medium">
                        <b>Upgrade to Field Pro</b>
                    </Text>
                    <Text size="small">This feature isn&apos;t available on your account.</Text>
                </Flex>
            );
        }

        const renderMessage = (message: IMessage, key: string) => {
            switch (message.type) {
                case MessageType.Text:
                    return (
                        <MessageRow
                            key={key}
                            isMine={message.isMine}
                            showAvatar={message.showAvatar}
                            date={message.date}
                            data-cy={`message-row-${key}`}
                        >
                            <MessageBubble isMine={message.isMine}>
                                {/* Local messages carry `text`; agent responses carry `data`. */}
                                <Text>{message.text ?? (message.data as string)}</Text>
                            </MessageBubble>
                        </MessageRow>
                    );
                case MessageType.RichText:
                    return (
                        <MessageRow
                            key={key}
                            isMine={message.isMine}
                            showAvatar={message.showAvatar}
                            date={message.date}
                            data-cy={`message-row-${key}`}
                        >
                            <MessageBubble isMine={message.isMine}>
                                <Text>
                                    {/* eslint-disable react/no-danger,@typescript-eslint/naming-convention */}
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html: message.text ?? (message.data as string) ?? '',
                                        }}
                                    />
                                    {/* eslint-enable react/no-danger,@typescript-eslint/naming-convention */}
                                </Text>
                            </MessageBubble>
                        </MessageRow>
                    );
                case MessageType.Markdown:
                    return (
                        <MessageRow
                            key={key}
                            isMine={message.isMine}
                            date={message.date}
                            showAvatar={message.showAvatar}
                            data-cy={`message-row-${key}`}
                        >
                            <MessageBubble isMine={message.isMine}>
                                <Markdown text={message.data as string} />
                            </MessageBubble>
                        </MessageRow>
                    );
                case MessageType.MidCallReport:
                    return (
                        <MessageRow
                            key={key}
                            isMine={message.isMine}
                            date={message.date}
                            showAvatar={message.showAvatar}
                            data-cy={`message-row-${key}`}
                        >
                            <ReportSections markdown={message.data as string} />
                        </MessageRow>
                    );
                case MessageType.Button:
                    return (
                        <MessageRow
                            key={key}
                            isMine={message.isMine}
                            showAvatar={message.showAvatar}
                            date={message.date}
                            data-cy={`message-row-${key}`}
                        >
                            <Button
                                appearance="primary"
                                onClick={message.onClick}
                                disabled={agentChatStore.loading || agentChatStore.regenerating}
                            >
                                {message.text}
                            </Button>
                        </MessageRow>
                    );
                case MessageType.Question:
                    return (
                        <MessageRow
                            key={key}
                            isMine={message.isMine}
                            showAvatar={message.showAvatar}
                            date={message.date}
                            data-cy={`message-row-${key}`}
                        >
                            <AgentSuggestedPrompts
                                prompts={message.data as string[]}
                                onSelect={handleMessage}
                            />
                        </MessageRow>
                    );
                default:
                    // Soft notice for message types this minimal agent view doesn't render yet.
                    return (
                        <MessageRow
                            key={key}
                            isMine={false}
                            showAvatar={false}
                            data-cy={`message-row-${key}`}
                        >
                            <MessageBubble isMine={false}>
                                <Text size="small">Unsupported message type.</Text>
                            </MessageBubble>
                        </MessageRow>
                    );
            }
        };

        return (
            <ChatLayout
                header={
                    <ChatHeaderNew
                        icon={showClose ? 'close' : 'back'}
                        onNavigationButtonClick={showClose ? handleClose : handleBack}
                    />
                }
                footer={
                    /*
                     * Unwrapped unless the demo button is showing, so the default footer layout is
                     * untouched for everyone else.
                     */
                    agentChatStore.demoMcciEnabled ? (
                        <Flex direction="column" gap="2">
                            <Button
                                appearance="secondary"
                                loading={agentChatStore.generatingDemo}
                                disabled={
                                    agentChatStore.loading ||
                                    agentChatStore.regenerating ||
                                    agentChatStore.status !== 'ready'
                                }
                                onClick={agentChatStore.generateDemo}
                                data-cy="generate-demo-mid-call-button"
                            >
                                Generate Demo
                            </Button>
                            <FooterRow
                                onMessageSend={handleMessage}
                                isLoading={agentChatStore.loading}
                                disabled={agentChatStore.status !== 'ready'}
                            />
                        </Flex>
                    ) : (
                        <FooterRow
                            onMessageSend={handleMessage}
                            isLoading={agentChatStore.loading}
                            disabled={agentChatStore.status !== 'ready'}
                        />
                    )
                }
            >
                {chatStore.messages.map((message, index) => {
                    return renderMessage(message, index.toString());
                })}
                {(agentChatStore.loading ||
                    agentChatStore.regenerating ||
                    agentChatStore.generatingDemo) && (
                    <MessageRow isMine={false}>
                        <Loader />
                    </MessageRow>
                )}
            </ChatLayout>
        );
    })
);
